import { GridCellProps } from '@progress/kendo-react-grid';
import { FC, useCallback, useMemo } from 'react';
import GridCellBase from 'src/components/GridCellBase';
import NumberInput from 'src/components/NumberInput';
import useDelayedInputOnchange from 'src/hooks/useDelayedInputOnchange';
import { isNullOrUndefined, round } from 'src/utils';
import { getBgColor } from 'src/utils/icons';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from '../InvoiceDetailsContext';
import OcrIndicator from '../split_line_item/OcrIndicator';

const PriceTextField: FC<GridCellProps> = (props: GridCellProps) => {
  const { dataItem } = props;
  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );
  const isLb = useMemo(() => String(dataItem?.uom).toLowerCase() === 'lb', [dataItem?.uom]);
  const { value, handleChange } = useDelayedInputOnchange(props);
  const getMatchingOcrLineItem = useContextSelector(
    InvoiceContext,
    (value) => value.getMatchingOcrLineItem
  );
  const handleHoverOcrField = useContextSelector(
    InvoiceContext,
    (value) => value.handleHoverOcrField
  );
  const initialInvoice = useContextSelector(InvoiceContext, (value) => value.initialInvoice);

  const newValue = round(Number(value), isLb ? 3 : 2);

  const errors = useMemo(() => dataItem?.errors?.priceAmt, [dataItem?.errors?.priceAmt]);
  const infos = useMemo(() => dataItem.informations?.priceAmt, [dataItem.informations?.priceAmt]);

  const formatted = '$' + newValue;

  const matchingItem = getMatchingOcrLineItem(dataItem.receiptLineItemId);
  const ocrConfidence = matchingItem.reduce(
    (acc, item) => (acc += parseFloat(item?.item?.itemPrice?.confidence || '0')),
    0
  );
  const averageOcrConfidence = round(ocrConfidence / matchingItem.length, 2);

  const isEdited = useMemo(() => {
    if (matchingItem.length === 1) {
      return (
        round(Number(matchingItem[0]?.item?.itemPrice?.value || 0), isLb ? 3 : 2) !==
        round(newValue, isLb ? 3 : 2)
      );
    } else if (matchingItem.length > 1) {
      const totalOcrItemPrice = matchingItem.reduce(
        (acc, item) =>
          (acc +=
            Number(item?.item?.itemPrice?.value || 0) *
            Number(item?.item?.itemQuantity?.value || 0)),
        0
      );
      const totalOcrQuantity = matchingItem?.reduce(
        (acc, item) => (acc += Number(item?.item?.itemQuantity?.value || 0)),
        0
      );
      const ocrItemPrice = round(totalOcrItemPrice / totalOcrQuantity, isLb ? 3 : 2);

      return ocrItemPrice !== newValue;
    }
    return false;
  }, [isLb, matchingItem, newValue]);
  const matchItemIndex = matchingItem.map(
    (item) => Number(item?.item?.itemSequenceNumber?.value) - 1
  );

  const handleHover = useCallback(() => {
    handleHoverOcrField('itemPrice', matchItemIndex);
  }, [handleHoverOcrField, matchItemIndex]);

  return (
    <GridCellBase {...props} className={getBgColor(errors, [], infos)} disableToolTip>
      <div className="flex flex-row gap-1 items-center justify-between">
        <div data-tooltip-id={'global-tooltip'} data-tooltip-content={formatted}>
          {isInvoiceEditDisabled ? (
            <span>{formatted}</span>
          ) : (
            <NumberInput
              valid={!errors?.length}
              value={isNullOrUndefined(value) ? '' : newValue.toString()}
              onChange={handleChange}
              prefix="$"
              decimal={isLb ? 3 : 2}
              onFocus={handleHover}
              ocr-data={`itemPrice:${matchItemIndex}`}
            />
          )}
        </div>
        {initialInvoice.ocrGenerated && ocrConfidence !== 0 && (
          <OcrIndicator
            isEdited={isEdited}
            ocrConfidence={`${averageOcrConfidence}%`}
            onHover={handleHover}
          />
        )}
      </div>
    </GridCellBase>
  );
};

export default PriceTextField;
